import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useFestivalTheme } from "@/context/FestivalThemeContext";

/**
 * FestFireworks — Diwali sky-rocket bursts behind the homepage hero.
 * Ported from the owner's paqvero-diwali-rockets-demo.html. Plain canvas +
 * rAF, no libraries. Diwali-period only: flip this one flag to remove it.
 * On/off is admin-controlled via the theme's decor.fireworks flag.
 */
const FIREWORKS_ENABLED = true;

/* Paqvero festive palette: gold, orange, cream, white, red-orange, amber */
const PAL = [[255, 190, 60], [255, 140, 30], [255, 230, 150], [255, 255, 255], [255, 95, 45], [255, 170, 40]];
/* light mode default: red family — the only family that reads on cream */
const PAL_LIGHT = [[211, 47, 47], [255, 70, 15], [164, 20, 20], [255, 150, 10], [255, 205, 195]];

const rnd = (a, b) => a + Math.random() * (b - a);
const hexRgb = (h) => {
  const m = String(h).replace("#", "");
  return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
};
const mixTo = (c, t, k) => c.map((v) => Math.round(v + (t - v) * k));
/* build a 5-stop palette from an admin-picked colour; light mode leans dark so sparks show on cream */
const paletteFor = (hex, light) => {
  const base = hexRgb(hex);
  return light
    ? [base, mixTo(base, 0, 0.25), mixTo(base, 0, 0.45), mixTo(base, 255, 0.2), [255, 255, 255]]
    : [base, mixTo(base, 255, 0.3), mixTo(base, 255, 0.55), [255, 255, 255], mixTo(base, 255, 0.15)];
};

export default function FestFireworks() {
  const { isFestival, decor } = useFestivalTheme();
  const { pathname } = useLocation();
  const canvasRef = useRef(null);
  const reduced = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  // on/off lives in Admin → Themes → Diwali → decor (fireworks flag) — admin-only
  const show = FIREWORKS_ENABLED && isFestival && pathname === "/" && Boolean(decor.fireworks);
  const fwColor = decor.fireworks_color || "";

  useEffect(() => {
    if (!show || reduced.current) return undefined;
    const c = canvasRef.current;
    if (!c) return undefined;
    const x = c.getContext("2d");
    const palDark = fwColor ? paletteFor(fwColor, false) : PAL;
    const palLight = fwColor ? paletteFor(fwColor, true) : PAL_LIGHT;
    const willowDark = fwColor ? palDark[1] : [255, 200, 90];
    const willowLight = fwColor ? palLight[0] : [211, 47, 47];

    let W = 0, H = 0;
    let rockets = [], parts = [], flashes = [];
    let rafId = null, loopTimer = null, secondTimer = null;
    let heroRect = null, lowQ = false, frameCount = 0, fpsStart = 0;
    const isLight = () => document.documentElement.classList.contains("light");
    const heroEl = () => document.querySelector('[data-testid="fest-hero-carousel"], [data-testid="promo-hero-carousel"]');

    function size() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = window.innerWidth; H = window.innerHeight;
      c.width = W * dpr; c.height = H * dpr;
      x.setTransform(dpr, 0, 0, dpr, 0, 0);
      measureHero();
    }
    function measureHero() {
      const el = heroEl();
      heroRect = el ? el.getBoundingClientRect() : null;
    }

    function heroVisible() {
      if (!heroRect) return false;
      return heroRect.bottom >= 160 && heroRect.top <= H * 0.8;
    }

    function launch() {
      if (document.hidden) return;
      measureHero(); // carousel loads async — re-measure every launch (cheap rect read)
      if (!heroVisible()) return;
      const cl = (W - 1160) / 2;
      let px;
      if (cl > 150 && Math.random() < 0.7) {
        px = Math.random() < 0.5 ? rnd(30, cl - 30) : rnd(W - cl + 30, W - 30);
      } else {
        px = rnd(W * 0.1, W * 0.9);
      }
      // dark: burst at the hero's bottom edge (spec). light: cards hide that spot,
      // so burst in the open sky band just above the cards instead (visibility).
      const ty = isLight()
        ? Math.max(100, heroRect.top + rnd(10, 60))
        : Math.max(90, Math.min(H * 0.86, heroRect.bottom) - rnd(0, 70));
      rockets.push({ x: px, y: H + 8, vx: rnd(-0.35, 0.35), vy: -rnd(12, 15), ty, tr: [] });
      startFrame();
    }

    function burst(bx, by) {
      const light = isLight();
      const pal = light ? palLight : palDark;
      const willowCol = light ? willowLight : willowDark;
      let n = W < 700 ? Math.round(rnd(30, 40)) : Math.round(rnd(50, 80));
      if (lowQ) n = Math.max(15, Math.round(n / 2));
      const type = Math.random(), base = pal[Math.floor(Math.random() * pal.length)];
      flashes.push({ x: bx, y: by, life: 1 });
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + rnd(-0.05, 0.05);
        const sp = type < 0.35 ? 4.4 : rnd(1.4, 5.2);
        const willow = type > 0.75;
        parts.push({
          x: bx, y: by, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1,
          decay: willow ? rnd(0.005, 0.008) : rnd(0.009, 0.018),
          col: willow ? willowCol : (Math.random() < 0.25 ? pal[Math.floor(Math.random() * pal.length)] : base),
          s: light ? rnd(1.7, 3.2) : rnd(1.2, 2.5), g: willow ? 0.06 : 0.045, tw: Math.random() < 0.35,
        });
      }
      for (let j = 0; j < 12; j++) { // white crackle
        parts.push({ x: bx, y: by, vx: rnd(-1.2, 1.2), vy: rnd(-1.2, 1.2), life: 1, decay: rnd(0.02, 0.035), col: [255, 255, 255], s: 1.2, g: 0.02, tw: true });
      }
      if (parts.length > 700) parts.splice(0, parts.length - 700);
    }

    function frame(now) {
      // fps guard: below ~45fps average, halve future spark counts
      frameCount += 1;
      if (frameCount === 1) fpsStart = now;
      if (frameCount >= 90) {
        lowQ = (frameCount / ((now - fpsStart) / 1000)) < 45;
        frameCount = 0;
      }
      const light = isLight();
      x.clearRect(0, 0, W, H);
      // additive glow pops on dark; on cream it washes out — paint normally there
      // (no hero clip in light mode: the cards would hide nearly every spark)
      x.globalCompositeOperation = light ? "source-over" : "lighter";
      const palNow = light ? palLight : palDark;
      const trailCol = "rgba(" + palNow[0].join(",") + ",";
      for (let i = rockets.length - 1; i >= 0; i--) {
        const k = rockets[i];
        k.x += k.vx; k.y += k.vy; k.vy *= 0.985;
        k.tr.push({ x: k.x, y: k.y }); if (k.tr.length > 16) k.tr.shift();
        for (let t = 1; t < k.tr.length; t++) {
          x.strokeStyle = trailCol + (t / k.tr.length * 0.7) + ")";
          x.lineWidth = 1 + (t / k.tr.length) * 1.8;
          x.beginPath(); x.moveTo(k.tr[t - 1].x, k.tr[t - 1].y); x.lineTo(k.tr[t].x, k.tr[t].y); x.stroke();
        }
        const g = x.createRadialGradient(k.x, k.y, 0, k.x, k.y, 9);
        if (light) { g.addColorStop(0, "rgba(" + palLight[1].join(",") + ",.95)"); g.addColorStop(1, "rgba(" + palLight[0].join(",") + ",0)"); }
        else { g.addColorStop(0, "rgba(255,240,200,.95)"); g.addColorStop(1, "rgba(" + palDark[0].join(",") + ",0)"); }
        x.fillStyle = g; x.fillRect(k.x - 9, k.y - 9, 18, 18);
        if (Math.random() < 0.5) parts.push({ x: k.x, y: k.y, vx: rnd(-0.3, 0.3), vy: rnd(0.2, 0.8), life: 1, decay: 0.06, col: [255, 170, 50], s: 1.1, g: 0, tw: false });
        if (k.y <= k.ty || k.vy > -2.2) { burst(k.x, k.y); rockets.splice(i, 1); }
      }
      for (let f = flashes.length - 1; f >= 0; f--) {
        const fl = flashes[f], fr = 60 + (1 - fl.life) * 40;
        const fg = x.createRadialGradient(fl.x, fl.y, 0, fl.x, fl.y, fr);
        const flashCore = light ? "rgba(" + palLight[0].join(",") + "," : "rgba(255,220,150,";
        fg.addColorStop(0, flashCore + fl.life * 0.5 + ")"); fg.addColorStop(1, "rgba(255,140,30,0)");
        x.fillStyle = fg; x.fillRect(fl.x - fr, fl.y - fr, fr * 2, fr * 2);
        fl.life -= 0.09; if (fl.life <= 0) flashes.splice(f, 1);
      }
      for (let p = parts.length - 1; p >= 0; p--) {
        const q = parts[p];
        q.vx *= 0.985; q.vy = q.vy * 0.985 + q.g; q.x += q.vx; q.y += q.vy; q.life -= q.decay;
        if (q.life <= 0) { parts.splice(p, 1); continue; }
        const al = q.life * (q.tw ? 0.5 + 0.5 * Math.sin(q.life * 60) : 1);
        x.fillStyle = "rgba(" + q.col[0] + "," + q.col[1] + "," + q.col[2] + "," + al + ")";
        x.beginPath(); x.arc(q.x, q.y, q.s * (0.6 + q.life * 0.6), 0, 6.283); x.fill();
      }
      x.globalCompositeOperation = "source-over";
      // stop the loop completely once the sky is empty
      if (rockets.length || parts.length || flashes.length) {
        rafId = requestAnimationFrame(frame);
      } else {
        rafId = null;
      }
    }
    function startFrame() { if (rafId === null) rafId = requestAnimationFrame(frame); }

    function loop() {
      launch();
      if (Math.random() < 1 / 3) secondTimer = setTimeout(launch, rnd(250, 600));
      loopTimer = setTimeout(loop, rnd(1800, 3400));
    }
    function stopAll() {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      clearTimeout(loopTimer); clearTimeout(secondTimer);
      rockets = []; parts = []; flashes = [];
      x.clearRect(0, 0, W, H);
    }
    function onVis() {
      if (document.hidden) stopAll();
      else { loopTimer = setTimeout(loop, 400); }
    }

    size();
    window.addEventListener("resize", size);
    window.addEventListener("scroll", measureHero, { passive: true });
    document.addEventListener("visibilitychange", onVis);
    loopTimer = setTimeout(loop, 700);

    return () => {
      stopAll();
      window.removeEventListener("resize", size);
      window.removeEventListener("scroll", measureHero);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [show, fwColor]);

  if (!show || reduced.current) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="fest-fireworks-canvas"
      className="pointer-events-none fixed inset-0 z-0 h-full w-full"
    />
  );
}
